import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useMatch, useNavigate, useParams } from "react-router-dom";

import { DashboardLayout } from "../../../components/layout";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import {
  getFreightFaqSectionEdit,
  storeFreightFaqSection,
  updateFreightFaqSection
} from "../../../utils/api";
import { showError, showSuccess } from "../../../utils/toast";
import "./adminFreight.css";

const FreightFaqSectionFormPage = () => {
  const navigate = useNavigate();
  const newMatch = useMatch({ path: "/freight/faq_section/new", end: true });
  const { id: routeId } = useParams();
  const isNew = Boolean(newMatch);
  const sectionId = isNew ? null : routeId;

  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = useMemo(
    () => [
      { label: roleLabel, link: "/dashboard" },
      { label: "Freight", link: "/freight" },
      { label: "FAQ sections", link: "/freight/faq_section" },
      { label: isNew ? "New section" : `Edit #${sectionId ?? ""}` }
    ],
    [roleLabel, isNew, sectionId]
  );

  const [loading, setLoading] = useState(!isNew);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [orderNo, setOrderNo] = useState("");
  const [status, setStatus] = useState("1");

  const loadEdit = useCallback(async () => {
    if (!sectionId) return;
    setLoading(true);
    try {
      const res = await getFreightFaqSectionEdit(sectionId);
      const d = res?.data?.freightFaqSectionData;
      if (!d) throw new Error("Section not found");
      setName(String(d.name ?? ""));
      setOrderNo(d.order_no != null ? String(d.order_no) : "");
      setStatus(String(d.status ?? "1"));
    } catch (e) {
      showError(e.message || "Load failed");
      navigate("/freight/faq_section", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate, sectionId]);

  useEffect(() => {
    if (isNew) {
      setLoading(false);
      return;
    }
    loadEdit();
  }, [isNew, loadEdit]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      showError("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      if (isNew) {
        await storeFreightFaqSection({
          name: trimmed,
          order_no: orderNo === "" ? undefined : Number(orderNo),
          status
        });
        showSuccess("Section created");
      } else {
        await updateFreightFaqSection({
          id: Number(sectionId),
          name: trimmed,
          order_no: orderNo === "" ? undefined : Number(orderNo),
          status
        });
        showSuccess("Section updated");
      }
      navigate("/freight/faq_section");
    } catch (err) {
      showError(err.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout breadcrumbs={breadcrumbs}>
        <div className="max-w-[720px] card offers-table p-6">Loading…</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="max-w-[720px]">
        <div className="card offers-table border-l-4 border-l-[#064E8D]">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h3 className="m-0">{isNew ? "New FAQ section" : "Edit FAQ section"}</h3>
            <Link to="/freight/faq_section" className="btn-outline no-underline text-sm">
              ← Back to list
            </Link>
          </div>

          <form className="freight-admin-form space-y-4" onSubmit={onSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={255}
                placeholder="e.g. General, Billing"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display order</label>
              <input
                type="number"
                min={0}
                value={orderNo}
                onChange={(e) => setOrderNo(e.target.value)}
                placeholder="Optional — lower shows first"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "Saving…" : isNew ? "Create section" : "Save changes"}
              </button>
              <Link to="/freight/faq_section" className="btn-outline no-underline inline-flex items-center">
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightFaqSectionFormPage;
